/**
 * Trigger Reverse SSI Lead Override (development / staging only)
 *
 * Calls the reverseSSIOverride cloud function to UNDO a Lead takeover of the
 * Semi-Structured Interview module and restore the original SSI clinician's
 * state from the ssiOverrideArchive snapshot.
 *
 * WHY THIS EXISTS
 * ---------------
 * The takeover (overrideSSIModule) is intentionally one-way in production — it
 * transfers effective SSI editing ownership to the Lead. That makes it awkward
 * to test repeatedly, because each test run permanently mutates the patient.
 * This reverse path is a TESTING CONVENIENCE, gated to non-production
 * environments on BOTH the client (button visibility) and the server (the CF
 * refuses to run in production projects), so QA can re-run the takeover flow on
 * the same patient instead of creating a fresh one each time.
 *
 * What the cloud function does:
 *   1. Validates the caller is the Lead and that a takeover is currently active
 *   2. Restores the SSI module, report section (outputs/approvals/inputs/edit
 *      history), report-approval flags and emerging formulation from the
 *      archive taken at takeover time
 *   3. Removes the leadOverride record so the original SSI clinician regains
 *      ownership, and clears the archive used
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Reverse (undo) a Lead takeover of the SSI module. Non-production only.
 *
 * @param {string} patientId - The patient ID
 * @returns {Promise<{success: boolean, message: string, error?: Error}>}
 */
export const TriggerReverseSSIOverride = async (patientId) => {
  console.log('TriggerReverseSSIOverride: Reversing SSI takeover for patient', patientId);

  try {
    const reverseSSIOverride = httpsCallable(functions, 'reverseSSIOverride');
    const result = await reverseSSIOverride({ patientId });

    console.log('SSI Override reversal successful:', result.data);

    return {
      success: true,
      message: result.data.message,
    };
  } catch (error) {
    console.error('Error reversing SSI override:', error);

    let errorMessage = 'An error occurred while reversing the SSI takeover.';

    if (error.code === 'functions/permission-denied') {
      errorMessage = 'Only the Lead clinician can reverse the SSI takeover.';
    } else if (error.code === 'functions/failed-precondition') {
      // Surface the CF's specific precondition message (e.g. reversal disabled
      // in production, no active takeover, missing archive).
      errorMessage = error.message || 'The SSI takeover cannot be reversed in its current state.';
    } else if (error.code === 'functions/not-found') {
      errorMessage = 'Patient or archive not found.';
    } else if (error.code === 'functions/unauthenticated') {
      errorMessage = 'You must be logged in to reverse the SSI takeover.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
      error,
    };
  }
};

export default TriggerReverseSSIOverride;
